import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type {
  Message,
  MessageWithConversationId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import type { ConversationTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { eq, useLiveInfiniteQuery, useLiveQuery } from "@tanstack/react-db";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { ConversationPresenceRecord } from "../db/collections";
import {
  fetchConversationMessages,
  fetchConversationThreads,
} from "../db/collections";
import { fetchSingleConversation } from "../db/conversation-list-collection";
import { filterGroupToTanstackExpression } from "../filters/conversation-tanstack-expressions";
import { useQueryOptions } from "../filters/filters-context";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";
import { DEFAULT_PAGE_SIZE } from "../table/types";

export interface UseConversationListResult {
  conversations: ConversationListItemResponse[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
}

export function useConversations(): {
  data: ConversationListItemResponse[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();
  const queryOptions = useQueryOptions();
  const { filterGroup } = queryOptions;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useLiveInfiniteQuery(
      (q) => {
        const baseQuery = q
          .from({ conversation: collections.conversationsList })
          .orderBy(({ conversation }) => conversation.lastCustomerMessageAt, {
            direction: "desc",
            nulls: "last",
          });

        return baseQuery.where(({ conversation }) => {
          const expr = filterGroupToTanstackExpression(
            filterGroup,
            conversation,
          );
          // TanStack requires a non-null expression; use tautology if no filter
          return expr ?? eq(conversation.id, conversation.id);
        });
      },
      { pageSize: DEFAULT_PAGE_SIZE },
      [queryOptions],
    );

  return { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading };
}

export function useConversation(conversationId: ConversationId): {
  data: ConversationListItemResponse | undefined;
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  const query = useQuery({
    queryKey: ["conversations", "single", conversationId],
    queryFn: async () => {
      const conversation = await fetchSingleConversation(
        effectApi,
        conversationId,
      );
      collections.conversationsList.utils.writeInsert(conversation);
      return conversation;
    },
    staleTime: 30_000,
  });

  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ conversation: collections.conversationsList })
        .where(({ conversation }) => eq(conversation.id, conversationId))
        .findOne(),
    [conversationId],
  );

  const data = liveResult.data ?? query.data;

  return { data, isLoading: query.isLoading };
}

export function useConversationThreads(conversationId: ConversationId): {
  data: ConversationThread[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  const query = useQuery({
    queryKey: ["conversations", "threads", conversationId],
    queryFn: async () => {
      const threads = await fetchConversationThreads(effectApi, conversationId);
      for (const thread of threads) {
        collections.threads.utils.writeUpsert(thread);
      }
      return threads;
    },
    staleTime: 30_000,
  });

  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ thread: collections.threads })
        .where(({ thread }) => eq(thread.conversationId, conversationId))
        .orderBy(({ thread }) => thread.createdAt, "asc"),
    [conversationId],
  );

  const data =
    liveResult.data.length > 0 ? liveResult.data : (query.data ?? []);

  return { data, isLoading: query.isLoading };
}

export function useConversationMessages(conversationId: ConversationId): {
  data: MessageWithConversationId[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  const query = useQuery({
    queryKey: ["conversations", "messages", conversationId],
    queryFn: async () => {
      const messages = await fetchConversationMessages(
        effectApi,
        conversationId,
      );
      for (const message of messages) {
        collections.messages.utils.writeInsert(message);
      }
      return messages;
    },
    staleTime: 30_000,
  });

  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.conversationId, conversationId)),
    [conversationId],
  );

  const liveData = liveResult.data ?? [];
  const data = liveData.length > 0 ? liveData : (query.data ?? []);

  return { data, isLoading: query.isLoading };
}

export function useThreadMessages(
  conversationId: ConversationId,
  threadId: string,
): { data: Message[]; isLoading: boolean } {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.conversationId, conversationId))
        .orderBy(({ message }) => message.sentAt, "asc"),
    [conversationId],
  );

  const data = useMemo(
    () => result.data.filter((m) => m.conversationThreadId === threadId),
    [result.data, threadId],
  );

  return { data, isLoading: result.isLoading };
}

export function useConversationTimelineEvents(conversationId: ConversationId): {
  data: ConversationTimelineEventResponse[];
  isLoading: boolean;
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ event: collections.timelineEvents })
        .where(({ event }) => eq(event.conversationId, conversationId))
        .orderBy(({ event }) => event.occurredAt, "desc"),
    [conversationId],
  );

  const data = result.data ?? [];

  return { data, isLoading: result.isLoading };
}

export function useConversationPresence(conversationId: ConversationId): {
  data: ConversationPresenceRecord[];
} {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ presence: collections.presence })
        .where(({ presence }) => eq(presence.conversationId, conversationId)),
    [conversationId],
  );

  return { data: result.data };
}

export function useSingleConversationData(conversationId: ConversationId): {
  conversation: ConversationListItemResponse | undefined;
  threads: ConversationThread[];
  messages: Message[];
  isLoading: boolean;
} {
  const conversationResult = useConversation(conversationId);
  const threadsResult = useConversationThreads(conversationId);
  const messagesResult = useConversationMessages(conversationId);

  return {
    conversation: conversationResult.data,
    threads: threadsResult.data,
    messages: messagesResult.data,
    isLoading:
      threadsResult.isLoading ||
      messagesResult.isLoading ||
      conversationResult.isLoading,
  };
}
